// src/components/blog/Blog.js
import React, { useState, useEffect } from 'react';
import ContactSection from '../contact/ContactSection';
import './Blog.css';
import './MobileBlog.css'; // Import mobile-specific styles

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Fetch the latest 6 posts from WordPress REST API
    const fetchPosts = async () => {
      try {
        setLoading(true);
        
        // Check if we're on hogoland.net domain to avoid CORS issues
        const currentDomain = window.location.hostname;
        const isOnHogolandDomain = currentDomain === 'hogoland.net' || currentDomain.endsWith('.hogoland.net');
        
        // Direct API URL
        const apiUrl = 'https://hogoland.net/blog/wp-json/wp/v2/posts?_embed&per_page=6';
        
        // Try to fetch with different approaches
        let response = null;
        let fetchError = null;
        
        // Approach 1: Try direct fetch first (works if same domain or CORS is configured)
        try {
          const directResponse = await fetch(apiUrl, {
            headers: {
              'Accept': 'application/json'
            },
            // Add mode: 'cors' explicitly
            mode: 'cors',
            // Add credentials: 'same-origin' for cookies if needed
            credentials: 'same-origin'
          });
          
          if (directResponse.ok) {
            response = directResponse;
          }
        } catch (error) {
          fetchError = error;
          console.warn("Direct fetch failed:", error);
        }
        
        // Approach 2: If direct fetch failed and not on hogoland domain, try with a proxy
        if (!response && !isOnHogolandDomain) {
          try {
            const proxyUrl = 'https://api.allorigins.win/raw?url=';
            const encodedUrl = encodeURIComponent(apiUrl);
            const proxyResponse = await fetch(proxyUrl + encodedUrl);
            
            if (proxyResponse.ok) {
              response = proxyResponse;
            }
          } catch (proxyError) {
            console.warn("Proxy fetch failed:", proxyError);
            fetchError = fetchError || proxyError;
          }
        }
        
        // If all fetch attempts failed, throw the error
        if (!response) {
          throw fetchError || new Error("All fetch attempts failed");
        }
        
        // Log success for debugging
        console.log("Successfully connected to WordPress API");
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform the WordPress data to match our component's expected format
        const formattedPosts = data.map(post => {
          // Get the featured media image URL or use a fallback
          let imageUrl = '/images/blog-placeholder.png'; // Default placeholder
          if (post._embedded && 
              post._embedded['wp:featuredmedia'] && 
              post._embedded['wp:featuredmedia'][0] &&
              post._embedded['wp:featuredmedia'][0].source_url) {
            imageUrl = post._embedded['wp:featuredmedia'][0].source_url;
          }
          
          // Format the date (from "2023-04-15T12:34:56" to "2023.04.15")
          const date = new Date(post.date);
          const formattedDate = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
          
          return {
            id: post.id,
            date: formattedDate,
            title: post.title.rendered, // The title is in HTML format, so we use .rendered
            imageUrl: imageUrl,
            link: post.link // The actual post URL
          };
        });
        
        setBlogPosts(formattedPosts);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError(err.message);
        setLoading(false);
        
        // Set fallback data in case of error with meaningful content
        // These examples are based on typical welfare and housing assistance topics
        setBlogPosts([
          {
            id: 1,
            date: '2025.03.25',
            title: '生活保護とは？生活保護の仕組みや受給する方法を簡単に解説',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/seikatsu-hogo-basics/',
          },
          {
            id: 2,
            date: '2025.03.20',
            title: '生活保護の申請方法：必要書類と審査プロセスの完全ガイド',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/seikatsu-hogo-application/',
          },
          {
            id: 3,
            date: '2025.03.15',
            title: '住宅扶助とは？生活保護受給者の住居費支援について',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/housing-assistance/',
          },
          {
            id: 4,
            date: '2025.03.10',
            title: '生活保護と就労：働きながら受給することは可能か',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/working-while-on-welfare/',
          },
          {
            id: 5,
            date: '2025.03.05',
            title: '医療扶助の範囲：生活保護受給者が受けられる医療サービス',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/medical-assistance/',
          },
          {
            id: 6,
            date: '2025.03.01',
            title: '生活保護受給中の引っ越し：住所変更手続きと注意点',
            imageUrl: '/images/blog-placeholder.png',
            link: 'https://hogoland.net/blog/moving-while-on-welfare/',
          }
        ]);
      }
    };
    
    fetchPosts();
  }, []);

  return (
    <section className="blog-section">
      {/* Main blog container - ブログ */}
      <div className="blog-container">
        {/* Frame 48098451 */}
        <div className="frame-48098451">
          {/* Frame 48098438 - Title and subtitle */}
          <div className="frame-48098438">
            <h2 className="blog-title">ブログ</h2>
            <p className="blog-subtitle">生活保護や住まいに関する疑問を解決するために、役立つ情報をまとめました。</p>
          </div>

          {/* Frame 48098450 - Blog posts grid and "Blog TOP" link */}
          <div className="frame-48098450">
            {/* Frame 48098442 - Blog posts grid */}
            <div className="frame-48098442">
              {loading ? (
                // Show loading state
                Array(6).fill().map((_, index) => (
                  <div key={index} className="blog-post-group loading">
                    <div className="frame-48098441">
                      <div className="rectangle-52 shimmer"></div>
                      <div className="frame-48098440">
                        <div className="frame-48098439">
                          <div className="blog-post-date shimmer"></div>
                          <div className="blog-post-title shimmer"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : error ? (
                // Show error state (but we should never reach this anymore)
                <div className="blog-error">
                  <p>ブログ記事の読み込みに問題が発生しました。再度お試しください。</p>
                </div>
              ) : (
                // Show blog posts
                blogPosts.map((post) => (
                  <div key={post.id} className="blog-post-group">
                    <a href={post.link} className="blog-post-link" target="_blank" rel="noopener noreferrer">
                      <div className="frame-48098441">
                        {/* Rectangle 52 - Image */}
                        <div className="rectangle-52">
                          <img 
                            src={post.imageUrl} 
                            alt={post.title} 
                            className="blog-post-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              // Try to use the public URL if available, otherwise fallback to absolute path
                              e.target.src = process.env.PUBLIC_URL ? 
                                `${process.env.PUBLIC_URL}/images/blog-placeholder.png` : 
                                '/images/blog-placeholder.png';
                            }}
                          />
                        </div>

                        {/* Frame 48098440 */}
                        <div className="frame-48098440">
                          {/* Frame 48098439 */}
                          <div className="frame-48098439">
                            {/* Date */}
                            <p className="blog-post-date">{post.date}</p>
                            {/* Article title - handle HTML entities */}
                            <h3 className="blog-post-title" 
                                dangerouslySetInnerHTML={{ __html: post.title }}></h3>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                ))
              )}
            </div>

            {/* Frame 149 - "Blog TOP" link */}
            {/* <div className="frame-149">
                <a 
                    href="#top" 
                    className="blog-top-link" 
                    onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                    }}
                >
                    ブログTOPへ
                </a>
            </div> */}
          </div>
        </div>

        {/* CTA Section (reusing ContactSection component) */}
        <div className="blog-contact-wrapper">
          <ContactSection />
        </div>
      </div>
    </section>
  );
};

export default Blog;